import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Building2, CheckCircle, ArrowRight } from "lucide-react";
import { useConsultationEnabled } from "@/hooks/useConsultationEnabled";

const GreenfieldService = () => {
  const consultationEnabled = useConsultationEnabled();
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Green-field Builds</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Build security into your infrastructure from day one. Design and implement secure systems from the ground up.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Security by Design</h2>
              <p className="mb-6">
                Green-field projects offer a unique opportunity to build security into your infrastructure from the very beginning. Our green-field build services ensure that security is not an afterthought but a fundamental part of your system architecture, saving time and money while delivering superior protection.
              </p>

              <h2 className="text-2xl font-bold mb-4">Cost Comparison</h2>
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <p className="font-semibold mb-3">Building security in from day one typically costs:</p>
                <p className="mb-2"><strong>30-40% less</strong> than retrofitting security 12 months later</p>
                <p className="text-sm text-muted-foreground">Avoiding expensive redesigns, security debt, and potential breach costs during vulnerable early phases</p>
              </div>

              <h2 className="text-2xl font-bold mb-4">What We Deliver</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security architecture documentation:</strong> Comprehensive diagrams, data flows, and threat models</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security baselines:</strong> Configuration standards for all infrastructure components</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Infrastructure as Code templates:</strong> Terraform/CloudFormation with security policies baked in</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security testing frameworks:</strong> Automated security testing integrated into CI/CD pipelines</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Compliance documentation:</strong> Evidence packages for ISO 27001, SOC 2, or industry standards</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Architecture Examples</h2>
              <div className="space-y-3 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Zero-Trust Network Access</p>
                  <p className="text-sm text-muted-foreground">Identity-centric architecture with continuous verification, microsegmentation, and least privilege access controls</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Cloud-Native Security</p>
                  <p className="text-sm text-muted-foreground">Multi-account AWS/Azure architecture with automated compliance, security-by-default configurations, and immutable infrastructure</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">DevSecOps Pipelines</p>
                  <p className="text-sm text-muted-foreground">Automated security testing in development, container security scanning, secrets management, and compliance gates</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">Recent Projects</h2>
              <div className="space-y-3 mb-6">
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Secure Cloud Migration - 200-User SaaS Company</p>
                  <p className="text-sm text-muted-foreground mb-1">Complete AWS environment with zero-trust network, automated compliance monitoring, and SOC 2 Type II ready architecture</p>
                  <p className="text-xs text-muted-foreground">Timeline: 6 weeks from design to deployment</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">OT/IT Network Segregation - Manufacturing Plant</p>
                  <p className="text-sm text-muted-foreground mb-1">Separated operational technology from IT networks with secure gateway, monitoring, and incident response capabilities</p>
                  <p className="text-xs text-muted-foreground">Result: 100% OT availability maintained while improving security posture</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Secure SaaS Architecture - Healthcare Startup</p>
                  <p className="text-sm text-muted-foreground mb-1">Multi-tenant platform with data isolation, encryption at rest and in transit, HIPAA-compliant logging and monitoring</p>
                  <p className="text-xs text-muted-foreground">Achieved HITRUST certification in 9 months</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">Built-In Security Controls</h2>
              <ul className="space-y-2">
                <li>✓ <strong>Infrastructure as Code</strong> with security policies and automated compliance checking</li>
                <li>✓ <strong>Security by default configurations</strong> eliminating common misconfiguration risks</li>
                <li>✓ <strong>Automated security testing</strong> integrated throughout development lifecycle</li>
                <li>✓ <strong>Immutable infrastructure</strong> preventing drift and unauthorised changes</li>
                <li>✓ <strong>Comprehensive logging</strong> and monitoring from day one</li>
              </ul>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Starting a new project?</h3>
              <p className="text-muted-foreground mb-6">
                Let's build security into your infrastructure from day one. Contact us to discuss your green-field project.
              </p>
              {consultationEnabled && (
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GreenfieldService;
